import {
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  type SignInOutput,
} from 'aws-amplify/auth'

import type { UserAttributes } from '@/shared/dtos'

type AuthError = Error & {
  code: string
}

function createAuthError(code: string, message: string): AuthError {
  const error = new Error(message) as AuthError
  error.code = code
  return error
}

/**
 * Replaces Auth.currentAuthenticatedUser() from v4.
 * Returns a shape compatible with the old CognitoUser object
 * so all consumers (CacheAdapter, navbar, permissions) work unchanged.
 */
export async function getAuthenticatedUser() {
  const [currentUser, session, attributes] = await Promise.all([
    getCurrentUser(),
    fetchAuthSession(),
    fetchUserAttributes(),
  ])

  if (!session.tokens?.idToken) {
    throw createAuthError(
      'NoAuthenticatedUser',
      'Authenticated user has no id token',
    )
  }

  return {
    ...currentUser,
    attributes: attributes as unknown as UserAttributes,
    authenticationFlowType: 'USER_SRP_AUTH' as const,
    signInUserSession: {
      idToken: {
        jwtToken: session.tokens.idToken.toString(),
        payload: session.tokens.idToken.payload as Record<string, unknown>,
      },
    },
  }
}

export async function getRequiredIdToken(): Promise<string> {
  const session = await fetchAuthSession()
  const idToken = session.tokens?.idToken

  if (!idToken) {
    throw createAuthError(
      'NoAuthenticatedUser',
      'Authenticated user has no id token',
    )
  }

  return idToken.toString()
}

export function assertSignInComplete(signInOutput: SignInOutput): void {
  if (signInOutput.isSignedIn) {
    return
  }

  const signInStep = signInOutput.nextStep.signInStep
  throw createAuthError(signInStep, `Sign in requires ${signInStep}`)
}
